// Client-side helper for the "previous game result" popup shown on
// lobby entry (Ludo, X&O, Rummy).
//
// The server persists an `acknowledged_at` column that HIDES the row
// from `/<game>/pending-result` once the user has seen it. The problem
// that kept popping up in practice: if the acknowledge POST lost the
// race (flaky network, auth hiccup, server blip) while the client had
// already hidden the popup, the next refresh fetched the row fresh
// and the popup reappeared — sometimes for hours.
//
// This helper adds a localStorage-backed "has been dismissed" set so
// once the user taps Back to Lobby, that session's popup is suppressed
// forever on that device even if the server-side update never landed.
// A silent background retry fires every time we load the app to flush
// pending acknowledgements to the server so the DB eventually catches
// up.

const KEY = 'pendingResult.acked';    // one shared key across all games

type AckMap = Record<string, string[]>;   // { game: [sessionId,...] }

function readMap(): AckMap {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') return parsed as AckMap;
  } catch {}
  return {};
}

function writeMap(map: AckMap) {
  try { localStorage.setItem(KEY, JSON.stringify(map)); } catch {}
}

/** Is the given session already marked as dismissed locally? */
export function isSessionAcked(game: string, sessionId: number | string): boolean {
  const map = readMap();
  const list = map[game] || [];
  return list.includes(String(sessionId));
}

/** Mark a session as dismissed locally. Idempotent. */
export function markSessionAcked(game: string, sessionId: number | string) {
  const map = readMap();
  const list = map[game] || [];
  const id = String(sessionId);
  if (!list.includes(id)) list.push(id);
  // Keep the list bounded so old sessions never bloat localStorage.
  map[game] = list.slice(-50);
  writeMap(map);
}

/** Remove a session from the local list (e.g. after the server ack
 *  succeeds so we don't keep silently retrying forever). */
export function clearSessionAcked(game: string, sessionId: number | string) {
  const map = readMap();
  const list = map[game] || [];
  const id = String(sessionId);
  map[game] = list.filter((s) => s !== id);
  writeMap(map);
}
